import React, { useState } from 'react';
import { X, ArrowLeft, ArrowRight, Check, Phone, Globe, Clock, Users } from 'lucide-react';

interface ReferralRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface FormData {
  phoneNumber: string;
  countryCode: string;
  fullName: string;
  timezone: string;
  morningTime: string;
  eveningTime: string;
  weekendMessaging: boolean;
  responseMethod: 'text' | 'voice' | 'both';
  friend1Phone: string;
  friend1CountryCode: string;
  friend1Name: string;
  friend2Phone: string;
  friend2CountryCode: string;
  friend2Name: string;
  friend3Phone: string;
  friend3CountryCode: string;
  friend3Name: string;
  hasPermission: boolean;
}

const countryCodes = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
];

const timezones = [
  'America/New_York (EST)',
  'America/Chicago (CST)',
  'America/Denver (MST)',
  'America/Los_Angeles (PST)',
  'Europe/London (GMT)',
  'Europe/Paris (CET)',
  'Europe/Berlin (CET)',
  'Asia/Tokyo (JST)',
  'Asia/Shanghai (CST)',
  'Asia/Kolkata (IST)',
  'Australia/Sydney (AEDT)',
];

export default function ReferralRegistrationModal({ isOpen, onClose, onComplete }: ReferralRegistrationModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<FormData>({
    phoneNumber: '',
    countryCode: '+1',
    fullName: '',
    timezone: 'America/New_York (EST)',
    morningTime: '08:00',
    eveningTime: '18:00',
    weekendMessaging: true,
    responseMethod: 'both',
    friend1Phone: '',
    friend1CountryCode: '+1',
    friend1Name: '',
    friend2Phone: '',
    friend2CountryCode: '+1',
    friend2Name: '',
    friend3Phone: '',
    friend3CountryCode: '+1',
    friend3Name: '',
    hasPermission: false,
  });

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 15;
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.phoneNumber.trim()) {
          newErrors.phoneNumber = 'Phone number is required';
        } else if (!validatePhoneNumber(formData.phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid phone number';
        }
        break;
      case 2:
        if (!formData.fullName.trim()) {
          newErrors.fullName = 'Full name is required';
        }
        if (!formData.timezone) {
          newErrors.timezone = 'Please select your timezone';
        }
        break;
      case 3:
        // Time validation is handled by input type="time"
        break;
      case 4:
        if (!formData.friend1Name.trim()) {
          newErrors.friend1Name = 'Friend 1 name is required';
        }
        if (!formData.friend1Phone.trim()) {
          newErrors.friend1Phone = 'Friend 1 phone number is required';
        } else if (!validatePhoneNumber(formData.friend1Phone)) {
          newErrors.friend1Phone = 'Please enter a valid phone number';
        }
        
        if (!formData.friend2Name.trim()) {
          newErrors.friend2Name = 'Friend 2 name is required';
        }
        if (!formData.friend2Phone.trim()) {
          newErrors.friend2Phone = 'Friend 2 phone number is required';
        } else if (!validatePhoneNumber(formData.friend2Phone)) {
          newErrors.friend2Phone = 'Please enter a valid phone number';
        }
        
        if (!formData.friend3Name.trim()) {
          newErrors.friend3Name = 'Friend 3 name is required';
        }
        if (!formData.friend3Phone.trim()) {
          newErrors.friend3Phone = 'Friend 3 phone number is required';
        } else if (!validatePhoneNumber(formData.friend3Phone)) {
          newErrors.friend3Phone = 'Please enter a valid phone number';
        }
        
        if (!formData.hasPermission) {
          newErrors.hasPermission = 'Permission confirmation is required';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    // Mock API call - replace with actual Supabase integration later
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setCurrentStep(5); // Success page
    } catch (error) {
      setErrors({ submit: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-4">
            {currentStep > 1 && currentStep < 5 && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <h2 className="text-xl font-semibold text-gray-900">
              {currentStep === 5 ? 'Welcome!' : 'Get 3 Weeks Free!'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Progress Bar */}
        {currentStep < 5 && (
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Step {currentStep} of 4</span>
              <span className="text-sm text-gray-600">{Math.round((currentStep / 4) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 4) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {/* Step 1: Phone Number Verification */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <Phone className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Phone Number Verification
                </h3>
                <p className="text-gray-600">
                  We'll send you daily prompts via WhatsApp
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex space-x-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => updateFormData('countryCode', e.target.value)}
                    className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => updateFormData('phoneNumber', formatPhoneNumber(e.target.value))}
                    placeholder="123-456-7890"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Basic Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <Globe className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Basic Information
                </h3>
                <p className="text-gray-600">
                  We'll personalize your experience
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => updateFormData('fullName', e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  {errors.fullName && (
                    <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => updateFormData('timezone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                  {errors.timezone && (
                    <p className="text-red-500 text-sm mt-1">{errors.timezone}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Messaging Preferences */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <Clock className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Messaging Preferences
                </h3>
                <p className="text-gray-600">
                  Set your preferred times for daily check-ins
                </p>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Morning Prompt
                    </label>
                    <input
                      type="time"
                      value={formData.morningTime}
                      onChange={(e) => updateFormData('morningTime', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Evening Check-in
                    </label>
                    <input
                      type="time"
                      value={formData.eveningTime}
                      onChange={(e) => updateFormData('eveningTime', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.weekendMessaging}
                      onChange={(e) => updateFormData('weekendMessaging', e.target.checked)}
                      className="w-5 h-5 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <span className="text-gray-700">Send messages on weekends</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Preferred Response Method
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'text', label: 'Text messages only' },
                      { value: 'voice', label: 'Voice messages only' },
                      { value: 'both', label: 'Both text and voice' },
                    ].map((option) => (
                      <label key={option.value} className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="responseMethod"
                          value={option.value}
                          checked={formData.responseMethod === option.value}
                          onChange={(e) => updateFormData('responseMethod', e.target.value as 'text' | 'voice' | 'both')}
                          className="w-4 h-4 text-emerald-500 border-gray-300 focus:ring-emerald-500"
                        />
                        <span className="text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Refer 3 Friends */}
          {currentStep === 4 && (
            <div className="space-y-6">
              {/* <div className="text-center">
                <Users className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Refer 3 Friends for Free Access
                </h3>
                <p className="text-gray-600">
                  Get 3 weeks free by sharing with friends
                </p>
              </div> */}

              <div className="space-y-6">
                {/* Friend 1 */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Friend 1</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        value={formData.friend1Name}
                        onChange={(e) => updateFormData('friend1Name', e.target.value)}
                        placeholder="Friend's name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      {errors.friend1Name && (
                        <p className="text-red-500 text-sm mt-1">{errors.friend1Name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <div className="flex space-x-2">
                        <select
                          value={formData.friend1CountryCode}
                          onChange={(e) => updateFormData('friend1CountryCode', e.target.value)}
                          className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          value={formData.friend1Phone}
                          onChange={(e) => updateFormData('friend1Phone', formatPhoneNumber(e.target.value))}
                          placeholder="123-456-7890"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                      {errors.friend1Phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.friend1Phone}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Friend 2 */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Friend 2</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        value={formData.friend2Name}
                        onChange={(e) => updateFormData('friend2Name', e.target.value)}
                        placeholder="Friend's name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      {errors.friend2Name && (
                        <p className="text-red-500 text-sm mt-1">{errors.friend2Name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <div className="flex space-x-2">
                        <select
                          value={formData.friend2CountryCode}
                          onChange={(e) => updateFormData('friend2CountryCode', e.target.value)}
                          className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          value={formData.friend2Phone}
                          onChange={(e) => updateFormData('friend2Phone', formatPhoneNumber(e.target.value))}
                          placeholder="123-456-7890"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                      {errors.friend2Phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.friend2Phone}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Friend 3 */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Friend 3</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        value={formData.friend3Name}
                        onChange={(e) => updateFormData('friend3Name', e.target.value)}
                        placeholder="Friend's name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      {errors.friend3Name && (
                        <p className="text-red-500 text-sm mt-1">{errors.friend3Name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <div className="flex space-x-2">
                        <select
                          value={formData.friend3CountryCode}
                          onChange={(e) => updateFormData('friend3CountryCode', e.target.value)}
                          className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          value={formData.friend3Phone}
                          onChange={(e) => updateFormData('friend3Phone', formatPhoneNumber(e.target.value))}
                          placeholder="123-456-7890"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                      {errors.friend3Phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.friend3Phone}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.hasPermission}
                      onChange={(e) => updateFormData('hasPermission', e.target.checked)}
                      className="w-4 h-4 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-700">I confirm I have their permission</span>
                  </label>
                  {errors.hasPermission && (
                    <p className="text-red-500 text-sm mt-1">{errors.hasPermission}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {currentStep === 5 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="h-10 w-10 text-emerald-500" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Welcome to OneWin.Today!
                </h3>
                <p className="text-gray-600 mb-4">
                  You'll receive your first morning prompt at{' '}
                  <span className="font-medium">
                    {new Date(`2000-01-01T${formData.morningTime}`).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                </p>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <p className="text-emerald-800 font-medium">🎉 3 weeks free access unlocked!</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Your friends will receive invitations to join OneWin.Today
                  </p>
                </div>
                <p className="text-sm text-gray-500">
                  Check your WhatsApp for a welcome message from us!
                </p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100">
          {currentStep < 5 ? (
            <button
              onClick={handleNext}
              disabled={isLoading}
              className="w-full bg-emerald-500 text-white py-3 rounded-xl hover:bg-emerald-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>
                    {currentStep === 1 ? 'Continue' : currentStep === 4 ? 'Complete Setup' : 'Continue'}
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={onComplete}
              className="w-full bg-emerald-500 text-white py-3 rounded-xl hover:bg-emerald-600 transition-colors font-medium"
            >
              Manage Preferences
            </button>
          )}
        </div>
      </div>
    </div>
  );
}